import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Component, forwardRef, Input, OnDestroy, OnInit, OnChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';

import { StaticDataService } from '~core/services/static-data.service';

@Component({
	selector: 'app-dynamic-input',
	templateUrl: './dynamic-input.component.html',
	providers: [
		{
			provide: [],
			useExisting: forwardRef(() => DynamicInputComponent),
			multi: true,
		},
	],
})
export class DynamicInputComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
	@Input() label?: string;
	@Input() placeholder = '';
	@Input() dataKey: string;
	@Input() disabled = false;
	@Input() multiple = false;
	// Selector of the element the options panel is attached to; set it where the field sits in a clipping scroll area
	@Input() appendTo: string | null = null;

	private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

	@ViewChild('ngSelect') ngSelectRef: NgSelectComponent;

	// An attached panel does not follow its field, so it closes when anything around it scrolls
	private closeOnScroll = (event: Event): void => {
		if (!(event.target instanceof Element && event.target.closest('.ng-dropdown-panel'))) {
			this.ngSelectRef?.close();
		}
	};

	public optionsLoading$: Subject<boolean> = new Subject();
	public options = [];
	public control: UntypedFormControl = new UntypedFormControl(null);
	public updateValue = (_: any) => {};
	public onTouched = () => {};

	constructor(
		public ngControl: NgControl,
		private readonly staticDataService: StaticDataService
	) {
		ngControl.valueAccessor = this;
	}

	private propagateChange(value: any): void {
		if (this.updateValue) {
			return this.updateValue(value);
		}

		if (this.control) {
			this.control.setValue(value, { emitEvent: false });
		}
	}

	public ngOnInit(): void {
		if (this.disabled) {
			this.control.disable({ emitEvent: false });
		}

		this.optionsLoading$.next(true);
		this.staticDataService.getStaticData(this.dataKey)
			.pipe(take(1))
			.subscribe((values: string[]) => {
				this.optionsLoading$.next(false);
				this.options = values.map((value) => ({
					value,
					label: value,
				}))
			})

		this.control.valueChanges.pipe(
			takeUntil(this.componentDestroyed$),
		).subscribe((value) => {
			this.propagateChange(value);
		});
	}

	public onOpen(): void {
		if (this.appendTo) {
			document.addEventListener('scroll', this.closeOnScroll, true);
		}
	}

	public onClose(): void {
		document.removeEventListener('scroll', this.closeOnScroll, true);
	}

	public trackByFn(index, item): void {
		return index;
	}

	public ngOnChanges(changes: any): void {
		if (changes.options) {
			// Update rendering or something
		}
	}

	public ngOnDestroy(): void {
		document.removeEventListener('scroll', this.closeOnScroll, true);
		this.componentDestroyed$.next(true);
		this.componentDestroyed$.complete();
	}

	public writeValue(value: any): void {
		// emitEvent: false — otherwise the initial writeValue propagates back
		// through valueChanges → onChange and marks the parent control dirty
		// on load, which makes validation errors appear before any interaction.
		setTimeout(() => this.control.setValue(value, { emitEvent: false }));
	}

	public registerOnChange(fn: any): void {
		this.updateValue = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean) {
		isDisabled
			? this.control.disable({ emitEvent: false })
			: this.control.enable({ emitEvent: false });
		this.disabled = isDisabled;
	}
}
